import { CanceledError, type InternalAxiosRequestConfig } from 'axios'

/**
 * Wait for `promise`, but reject with axios' cancel error once the request's own signal aborts.
 * The shared promise keeps running for the other requests waiting on it.
 */
export function awaitWithinRequestSignal<T>(promise: Promise<T>, config: InternalAxiosRequestConfig): Promise<T> {
  const { signal } = config

  if (!signal?.addEventListener)
    return promise

  if (signal.aborted) {
    // This caller stops waiting, but a later failure of the shared promise must still be observed.
    promise.catch(() => {})

    return Promise.reject(new CanceledError(undefined, config))
  }

  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(new CanceledError(undefined, config))

    signal.addEventListener?.('abort', onAbort)
    promise
      .then(resolve, reject)
      .finally(() => signal.removeEventListener?.('abort', onAbort))
  })
}
